import useSWR from 'swr';
import { useEffect, useState } from 'react';
import { EnabledChains, Trade, Trader, User } from '../../../config/constants';
import { useWeb3React } from '@web3-react/core';
import { verifyChainConfig, subgraphQuery } from '../../../helpers';
import { BigNumber } from 'ethers';
import { formatUnits, parseUnits } from 'ethers/lib/utils';
import useDelayLoading from '../../useDelayLoading';
import useTokenDetails from '../useTokenDetails';
import { config } from '../../../config/config';
import moment from 'moment';

export type TradeHistory = Trade & {
    volume: string;
    closePrice: string;
    entryPrice: string;
    isShort: boolean;
    leverage: string;
    margin: string;
    pnl: string;
    pnlPercent: string;
    netValue: string;
};

let initialLoadUp = false;

const MAX_LENGTH = 100;

// very similar to usePlatformPositions
// separating into its own hook for custom cache and revalidation config
const useTradeHistory = (rowPerpage: number = 10) => {
    const { chainId } = useWeb3React();
    const [, setHasNextPage] = useState(true);
    const [currentPage, setCurrentPage] = useState(0);

    const delayComplete = useDelayLoading();

    // const { decimals } = useTokenDetails(
    //     config.addresses[chainId as EnabledChains[number]]?.collateral
    // );

    const { data, error, mutate } = useSWR(
        chainId
            ? // && decimals
              [
                  'tradeHistory',
                  chainId,
                  //  decimals
              ]
            : null,
        async (
            _,
            chainId
            //  decimals
        ): Promise<TradeHistory[] | undefined> => {
            if (!verifyChainConfig(chainId)) return;

            // const data = await fetchTradeHistory(MAX_LENGTH, chainId, 0);

            const data: Trade[] = [
                {
                    borrowFee: '123',
                    borrowFeePnL: '12',
                    closePrice: '1230',
                    entryMargin: '200',
                    entryPrice: '2000',
                    fundingFee: '12',
                    fundingFeePnL: '12',
                    grossPnL: '123',
                    id: '0x',
                    percentageFeePnL: '1',
                    percentageGrossPnL: '2',
                    percentagePnL: '2',
                    PnL: '3',
                    status: 'CLOSED',
                    timestamp: moment().startOf('day').unix(),
                    totalFeePnL: '123',
                    tradeIndex: 1,
                    tradePair: {
                        active: true,
                        assetDecimals: 18,
                        borrowFeeRate: '12',
                        collateral: {
                            decimals: 6,
                            id: '0x',
                            liquidity: '123',
                            liquidityPools: [],
                            protocolFees: {
                                dev: '1',
                                hourly: [],
                                id: '0x',
                                insuranceFund: '1',
                                liquidityPool: '1',
                                platform: '1',
                                referrer: '1',
                                staker: '1',
                                total: '2',
                                whiteLabel: '3',
                            },
                            tradingVolume: '12',
                        },
                        dataMapping: {
                            data: [],
                            id: '0x',
                        },
                        feeBufferFactor: '12',
                        id: '0x',
                        liquidatorReward: '11',
                        liquidityPoolAdapter: {
                            active: true,
                            id: '0x',
                            liquidityPools: [],
                            tradePairs: [],
                        },
                        maxExcessRatio: '1',
                        maxFundingFeeRate: '2',
                        maxLeverage: '100',
                        minLeverage: '0',
                        minMargin: '10',
                        name: 'BTC/USD',
                        positions: [],
                        priceFeedAdapter: {
                            active: true,
                            id: '0x',
                        },
                        totalAssetAmountLimit: '100',
                        volumeLimit: '1000',
                    },
                    trader: {} as Trader,
                    txHash: '0x',
                    type: 'CLOSED',
                    user: {} as User,
                    volume: '10',
                },
            ];

            return formatPositions(data, 18);
        },
        {
            refreshInterval: 10000,
            dedupingInterval: 0,
        }
    );

    const loadMoreData = async () => {
        if (!verifyChainConfig(chainId) || currentPage + 1 === MAX_LENGTH / rowPerpage)
            return;

        const data = await fetchTradeHistory(
            rowPerpage,
            chainId,
            currentPage * rowPerpage
        );

        setCurrentPage((prev) => prev + 1);

        if (data.length < rowPerpage) {
            setHasNextPage(false);
        }

        const formattedData = formatPositions(data, 18);

        await mutate(
            (history) => {
                if (history && history.length === MAX_LENGTH - rowPerpage) {
                    setHasNextPage(false);
                }

                return history ? [...history, ...formattedData] : formattedData;
            },
            {
                revalidate: false,
            }
        );
    };

    useEffect(() => {
        if (delayComplete) {
            initialLoadUp = true;
        }
    }, [delayComplete]);

    return {
        hasNextPage: false,
        tradeHistory: data,
        isLoading: (!data && !error) || (!initialLoadUp && !delayComplete),
        error,
        loadMoreData,
    };
};

export default useTradeHistory;

const getQuery = (first: number, skip: number) => `
    trades(
        first: ${first}, 
        skip: ${skip}, 
        orderBy: timestamp, 
        orderDirection: desc
        where: {
            timestamp_gt : 1675177200
        }
    ) {
        id
        PnL
        type
        status
        volume
        timestamp
        tradeIndex
        entryPrice
        closePrice
        entryMargin
        percentagePnL
        positionId
        tradePair {
            id
            collateral {
                decimals
            }
        }
    }
`;

const fetchTradeHistory = async (
    rowPerPage: number,
    chainId: number,
    skip: number = 0
) => {
    const query = getQuery(rowPerPage, skip);

    const data: {
        trades: Trade[];
    } = await subgraphQuery(query, { chainId });

    return data.trades;
};

const formatPositions = (trades: Trade[], decimals?: number): TradeHistory[] =>
    trades.map((trade) => {
        let leverage = parseUnits('0');

        if (!BigNumber.from(trade.entryMargin).isZero()) {
            leverage = BigNumber.from(trade.volume)
                .mul(parseUnits('1', decimals))
                .div(trade.entryMargin);
        }

        const netValue = BigNumber.from(trade.entryMargin).add(
            parseUnits(trade.PnL.toString(), decimals)
        );

        return {
            ...trade,
            volume: formatUnits(trade.volume, decimals),
            closePrice: formatUnits(trade.closePrice, config.priceFeedDecimal),
            entryPrice: formatUnits(trade.entryPrice, config.priceFeedDecimal),
            isShort: trade.type === 'SHORT',
            leverage: formatUnits(leverage, decimals),
            margin: formatUnits(trade.entryMargin, decimals),
            pnl: formatUnits(trade.PnL, decimals),
            pnlPercent: trade.percentagePnL.toString(),
            netValue: formatUnits(netValue, decimals),
        };
    });
